/**
 * src/types/index.ts — TypeScript entity interfaces for Attic Dev.
 * Mirrors HarperDB schema; no `any` types allowed.
 *
 * WP-ATTICDEV-015-CRATE: CrateStatus extended to 6-state canonical lifecycle;
 *   Crate gains category, cover_photo_reference, activity_log;
 *   Order gains crate_ids, delivery_instructions.
 * WP-ATTICDEV-031-ROLE (T1, Story 1): CustomerRole union type introduced.
 * WP-ATTICDEV-031-ROLE (AC-004, Story 3): Customer.role narrowed from
 *   string | null to CustomerRole | null. TypeScript strict mode now
 *   rejects any value not in the union at compile time.
 */

/**
 * Account role for Attic users (WP-ATTICDEV-031-ROLE).
 *
 * customer — default storage customer.
 * driver   — delivery driver (access to driver portal features).
 * both     — has both customer and driver privileges.
 */
export type CustomerRole = 'customer' | 'driver' | 'both' | 'admin';

/**
 * Safely narrows a raw DB string to CustomerRole.
 * Returns null for legacy values (e.g. 'admin') and missing fields.
 * Useful for migrating legacy records or validating external input.
 */
export function narrowToCustomerRole(
  value: string | null | undefined,
): CustomerRole | null {
  if (value === 'customer' || value === 'driver' || value === 'both' || value === 'admin') return value;
  return null;
}

/**
 * Crate lifecycle states.
 *
 * available         → In warehouse inventory, not assigned to any customer.
 * ordered           → Assigned to a customer, delivery job created.
 * at_home           → Crate is at the customer's location.
 * pickup_scheduled  → Customer has scheduled a pickup; driver assigned.
 * in_transit        → Crate is in transit (to/from warehouse).
 * stored            → Crate is in the Attic warehouse (customer's stuff inside).
 * return_scheduled  → Customer has requested retrieval; driver assigned.
 */
export type CrateStatus =
  | 'available'
  | 'ordered'
  | 'at_home'
  | 'pickup_scheduled'
  | 'in_transit'
  | 'stored'
  | 'return_scheduled'
  // Routing v1: customer returned empties; driver did the empty_pickup
  // scan, ownership detached. Driver inspects in-van (clean + empty)
  // before flipping back to 'available' (crate joins the empty stack)
  // or to 'needs_cleaning' (returned to facility for ops).
  | 'inspecting'
  | 'needs_cleaning';

export type ConnectionStatus = 'live' | 'reconnecting' | 'offline';

export interface Customer {
  id: string;
  uuid?: string;
  email: string;
  name: string;
  address: string | null;
  /** Phone number — optional field pending schema migration (WP-ATTICDEV-001-CUSTOMER) */
  phone?: string | null;
  billing_tier?: string | null;
  /**
   * role: account role for this customer (WP-ATTICDEV-031-ROLE AC-004).
   * Narrowed to CustomerRole | null in Story 3. TypeScript strict mode rejects
   * any value outside the CustomerRole union at compile time.
   * Legacy records with no role field yield undefined (treated as null by callers).
   * Note: the legacy 'admin' value (WP-ATTICDEV-015-CRATE) is no longer emitted
   * by the app; CrateDetailScreen.isAdmin is permanently false for new records.
   */
  role?: CustomerRole | null;
  /** Profile picture as a base64 data URL (or null → initials avatar). */
  avatar_url?: string | null;
  /** Standing pickup handoff preference — overridable per order. */
  default_pickup_method?: 'porch_pickup' | 'doorbell' | null;
  /**
   * Gate/building access code the driver needs to reach this customer. The
   * Order-level field of the same name overrides this when set per-order.
   */
  gate_code?: string | null;
  /**
   * Buzzer or apartment label ("Apt 3", "Buzz #3"). Rendered next to the
   * address on the driver's stop card. Order-level override of same name.
   */
  buzzer_apartment?: string | null;
  /**
   * Free-form standing instructions ("dog is friendly, leave on back
   * porch"). Order-level `delivery_instructions` overrides this per-order.
   */
  special_instructions?: string | null;
  /**
   * Identity provider this account authenticates with. Null on legacy
   * accounts (pre-Clerk); the AuthClerk resource rotates to the correct
   * value on first Clerk sign-in. Future sign-ins enforce: a Google
   * account can't log in via email, and vice versa.
   */
  auth_provider?: 'email' | 'google' | null;
  /** Clerk user id (usr_xxx) — populated after first Clerk sign-in. */
  clerk_user_id?: string | null;
  /**
   * Admin-toggleable flag that unlocks dev/test affordances (currently the
   * "simulate crate scan" button in the driver active page). Off by default.
   * Server endpoints that expose test-only shortcuts re-check this on the
   * authenticated user before acting — it's not a pure client affordance.
   */
  test_features_enabled?: boolean | null;
  /**
   * Address environment for the planner's service-time matrix
   * (EP-DELIVERY-ROUTING-V1). Required at signup for new customers.
   * Legacy records may have null until next profile edit.
   */
  location_type?:
    | 'sfh_ground'
    | 'sfh_stairs'
    | 'townhome'
    | 'apt_elevator'
    | 'apt_walkup'
    | 'downtown'
    | 'other'
    | null;
  /** Floor number for apt_elevator / apt_walkup. Null otherwise. */
  floor_number?: number | null;
  /**
   * Account-closure state (Phase 1 — Confluence "Crate Cancellation &
   * Wind-Down"). null/'active' = normal; 'closing' = wind-down in progress
   * (new crate orders blocked, billing stopped, final return scheduled);
   * 'closed' reserved for Phase 2. Server-managed — transitions only via
   * POST /CloseAccount and /ReopenAccount.
   */
  account_state?: 'active' | 'closing' | 'closed' | null;
  /** ISO timestamp the closure was initiated; null unless closing. */
  closure_started_at?: string | null;
  /**
   * Denormalized cluster assignment from address geocode. Set on signup,
   * recomputed when address changes. Drives the planner's order pull.
   */
  cluster_id?: string | null;
  updated_at?: string | null;
  created_at: string;
}

/**
 * Cluster — neighborhood-scale routing territory served by 1-2 Facilities.
 * Drivers anchor to a cluster for their shift; the planner pulls orders only
 * for the driver's cluster. Hand-tuned centroid + radius today; future work
 * may switch to polygons or Voronoi from facility centroids.
 * (EP-DELIVERY-ROUTING-V1)
 */
export interface Cluster {
  id: string;
  uuid?: string;
  name: string;
  lat: number;
  lng: number;
  radius_mi: number;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface Crate {
  id: string;
  /** uuid is the raw HarperDB primary key — normalizeRecords maps it to id */
  uuid?: string;
  customer_id: string;
  status: CrateStatus;
  qr_code: string;
  /** Customer-editable pretty name (e.g. "Winter Clothes"). Empty/null on a
   *  fresh crate — the UI falls back to "Crate #<sequence_num>" via crateDisplayName. */
  label?: string | null;
  /** Per-customer 1-based index assigned at crate creation. Used for display
   *  when `label` is empty so users never see a raw UUID. */
  sequence_num?: number | null;
  /** Free-form description of what's inside the crate. Replaces the old
   *  per-item Item table — customers describe contents in one sentence
   *  instead of itemizing. Searchable from the customer search screen. */
  contents?: string | null;
  location?: string | null;
  /**
   * category: customer-assigned crate category for My Storage browse/filter.
   * One of: Holiday | Seasonal | Kids | Kitchen | Clothing | Documents | Other
   * (WP-ATTICDEV-015-CRATE AC-001, AC-005)
   */
  category?: string | null;
  /**
   * cover_photo_reference: base-64 data URL or URL to the crate's cover photo.
   * Displayed as banner on CrateDetailScreen and thumbnail in My Storage.
   * (WP-ATTICDEV-015-CRATE AC-001, AC-005)
   */
  cover_photo_reference?: string | null;
  /**
   * activity_log: JSON-serialized array of { event: string; timestamp: string }.
   * Append-only audit trail of crate status transitions.
   * (WP-ATTICDEV-015-CRATE AC-001, AC-009)
   */
  activity_log?: string | null;
  /**
   * crate_number: THE printed identity — letter + six digits ("A000123").
   * Globally unique, assigned once at pool-crate creation, stamped on all
   * four labels. Null only on rows minted before the counter shipped and
   * not yet backfilled (scripts/backfill-crate-numbers.mjs).
   */
  crate_number?: string | null;
  /**
   * arrival_unread: true when the crate has just transitioned to `at_home`
   * or `stored` and the customer hasn't yet acknowledged it by tapping
   * through to the detail page. Powers the "Arrived!" / "Tucked in!"
   * pill on CrateCard. Server sets true on those transitions + false on
   * any other status transition; client sets false when the crate detail
   * view is opened. Undefined on legacy records — treated as false.
   */
  arrival_unread?: boolean;
  /**
   * transit_direction: which way the crate is moving while `status` is
   * `in_transit`. Server sets this on the facility_load / pickup scan
   * paths in ValidateCrateScan, clears it to null on every terminal
   * transition (at_home, stored, scheduled states). Read by
   * CrateJourneyTracker to orient the compact dots + trail so
   * storage→home retrievals and new-crate deliveries render correctly.
   * Null / undefined on legacy records — tracker falls back to the old
   * activity-log heuristic for those.
   */
  transit_direction?: 'to_home' | 'to_storage' | null;
  /**
   * stored_at: ISO timestamp the crate entered `stored` (set at the facility
   * stow scan). Anchors the 4-month free-retrieval clock — free-retrieval
   * date = stored_at + 4 calendar months (end-of-month clamped). Reset on
   * each fresh stow so a re-store restarts the clock; untouched by
   * mid-storage re-scans. Null/undefined until first stowed (and on legacy
   * records) — eligibility helpers treat that as "no clock yet".
   */
  stored_at?: string | null;
  created_at: string;
}

// (Item interface removed — replaced by Crate.contents text field.)

export interface Order {
  id: string;
  customer_id: string;
  crate_id: string;
  type: string;
  status: string;
  scheduled_at: string | null;
  /**
   * crate_ids: array of crate IDs for retrieval orders (multi-crate return requests).
   * null for legacy single-crate pickup orders.
   * (WP-ATTICDEV-015-CRATE AC-001, AC-007, AC-008)
   */
  crate_ids?: string[] | null;
  /**
   * delivery_instructions: optional customer note attached to an order.
   * Overrides Customer.special_instructions on the driver's stop card
   * when set per-order (otherwise driver sees the customer's standing
   * instructions).
   */
  delivery_instructions?: string | null;
  /**
   * gate_code: per-order override of Customer.gate_code. Null/absent
   * means "use the customer's standing default."
   */
  gate_code?: string | null;
  /**
   * buzzer_apartment: per-order override of Customer.buzzer_apartment.
   */
  buzzer_apartment?: string | null;
  created_at: string;
}

export interface Subscription {
  id: string;
  customer_id: string;
  plan: string;
  status: string;
  started_at: string;
  expires_at: string;
}

export interface Notification {
  id: string;
  customer_id: string;
  type: string;
  message: string;
  read: boolean;
  created_at: string;
}

/** Pub/Sub event shape arriving over HarperDB WebSocket channel */
export interface CratePubSubEvent {
  type: 'crate_update';
  data: Crate;
}
